import React, { createContext, useContext, useEffect } from 'react'

import usePersistedState from './usePersistedState'
import clientConfig from '../services/clientConfig'
import {
  DEFAULT_ADESAO_CONFIG,
  fetchAdesaoConfig,
  normalizeAdesaoConfig,
} from '../services/adesaoConfig'
import { AdesaoConfiguracao } from '../utils/interfaces'

interface AdesaoConfigContextData {
  adesaoConfig: AdesaoConfiguracao
}

const AdesaoConfigContext = createContext<AdesaoConfigContextData | undefined>(
  undefined,
)

const AdesaoConfigProvider: React.FC = ({ children }) => {
  const { codCliente, tipo, plano } = clientConfig()
  const [adesaoConfig, setAdesaoConfig] = usePersistedState<AdesaoConfiguracao>(
    'adesaoConfig',
    DEFAULT_ADESAO_CONFIG,
  )

  useEffect(() => {
    let mounted = true

    setAdesaoConfig(normalizeAdesaoConfig(DEFAULT_ADESAO_CONFIG))

    async function loadAdesaoConfig() {
      try {
        const config = await fetchAdesaoConfig({ codCliente, tipo, plano })

        if (mounted) {
          setAdesaoConfig(normalizeAdesaoConfig(config))
        }
      } catch {
        if (mounted) {
          setAdesaoConfig(normalizeAdesaoConfig(DEFAULT_ADESAO_CONFIG))
        }
      }
    }

    loadAdesaoConfig()

    return () => {
      mounted = false
    }
  }, [codCliente, plano, setAdesaoConfig, tipo])

  return (
    <AdesaoConfigContext.Provider value={{ adesaoConfig }}>
      {children}
    </AdesaoConfigContext.Provider>
  )
}

function useAdesaoConfig(): AdesaoConfigContextData {
  const context = useContext(AdesaoConfigContext)

  if (!context) {
    throw new Error('useAdesaoConfig must be used within an AdesaoConfigProvider')
  }

  return context
}

export { AdesaoConfigProvider, useAdesaoConfig }
